import { useCallback, useEffect, useRef, useState } from 'react'
import { ActionIcon, Collapse, Group, Text } from '@mantine/core'
import { useReducedMotion } from '@mantine/hooks'
import { IconInfoCircle } from '@tabler/icons-react'

export interface EstimateComparisonProps {
  /** Template rollup hours (base) */
  baseHours: number
  baseCostMin?: number | null
  baseCostMax?: number | null
  /** When false, nothing is rendered (parent gates on numeric diff vs. personalized). */
  visible: boolean
  /** `useIntersection` on quick stats row — false when scrolled past (Story 2.9 sentinel). */
  isEstimatesInView: boolean
}

function formatBaseEstimateLine(
  baseHours: number,
  baseCostMin: number | null | undefined,
  baseCostMax: number | null | undefined
): string {
  const hoursPart = `${baseHours} hours`
  const hasCost =
    baseCostMin != null ||
    baseCostMax != null
  if (!hasCost) {
    return hoursPart
  }
  const min = baseCostMin ?? 0
  const max = baseCostMax ?? baseCostMin ?? 0
  const costPart = `$${min} - $${max}`
  return `${hoursPart}, ${costPart}`
}

export function EstimateComparison({
  baseHours,
  baseCostMin,
  baseCostMax,
  visible,
  isEstimatesInView,
}: EstimateComparisonProps) {
  const prefersReducedMotion = useReducedMotion(false, { getInitialValueInEffect: false })
  const [expandedWhileAway, setExpandedWhileAway] = useState(false)
  const prevEstimatesInView = useRef<boolean | undefined>(undefined)

  const line = formatBaseEstimateLine(baseHours, baseCostMin, baseCostMax)

  // Re-collapse: (1) estimates back in view → reset; (2) user was in view then left again → same rules as AC2
  useEffect(() => {
    if (isEstimatesInView) {
      setExpandedWhileAway(false)
    } else if (prevEstimatesInView.current === true) {
      setExpandedWhileAway(false)
    }
    prevEstimatesInView.current = isEstimatesInView
  }, [isEstimatesInView])

  // (3) Expanded while past header: re-collapse on coalesced scroll (200ms), not every scroll tick
  useEffect(() => {
    if (isEstimatesInView || !expandedWhileAway) return

    let debounceId: ReturnType<typeof setTimeout> | undefined
    const onScroll = () => {
      if (debounceId !== undefined) return
      debounceId = setTimeout(() => {
        debounceId = undefined
        setExpandedWhileAway(false)
      }, 200)
    }
    const win = window
    win.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      if (debounceId !== undefined) clearTimeout(debounceId)
      win.removeEventListener('scroll', onScroll)
    }
  }, [isEstimatesInView, expandedWhileAway])

  const showFullSentence = isEstimatesInView || expandedWhileAway
  const collapseDuration = prefersReducedMotion ? 0 : 200

  const expand = useCallback(() => {
    setExpandedWhileAway(true)
  }, [])

  if (!visible) return null

  return (
    <Group gap="xs" wrap="wrap" align="flex-start" data-testid="estimate-comparison">
      {!showFullSentence && (
        <ActionIcon
          type="button"
          variant="subtle"
          color="gray"
          size="sm"
          radius="xl"
          aria-label="Show base estimate comparison"
          onClick={expand}
        >
          <IconInfoCircle size={18} aria-hidden />
        </ActionIcon>
      )}

      <Collapse in={showFullSentence} transitionDuration={collapseDuration}>
        <Group gap={6} wrap="nowrap" align="center">
          <IconInfoCircle
            size={16}
            aria-hidden
            style={{ color: 'var(--mantine-color-base-estimate-6)', flexShrink: 0 }}
          />
          <Text
            size="sm"
            data-testid="estimate-comparison-line"
            style={{ color: 'var(--mantine-color-base-estimate-6)' }}
          >
            <Text span fw={600} inherit>
              Base estimate:
            </Text>{' '}
            {line}
          </Text>
        </Group>
      </Collapse>
    </Group>
  )
}
